import * as THREE from 'three';
import type { IDXFRenderContext, IDXFTextInfo } from '@/editor/floor-plan/dxf/types';
import { globalStores } from '@/editor/stores';
import { pixelRatio2d } from '@/editor/math/const';
import { EEntityType } from '@/editor/types';
import BaseModel2d from './BaseModel2d';
import TextCanvasMesh from '@/editor/floor-plan/dxf/utils/TextCanvasMesh';
import { setModelVisible } from '../utils';
import { hoverOutlineColor } from '@/editor/constant/entity';
import { IPoint2d } from '@/editor/math/IPoint2d';

// 设计给的底图配色，线条与文字分开取色
const LAYER_LINE_COLOR = 0xa38aff;
const LAYER_TEXT_COLOR = 0x0b0113;
const LAYER_HIGHLIGHT_COLOR = hoverOutlineColor;

const LAYER_DEFAULT_TEXT_COLOR = `#${LAYER_TEXT_COLOR.toString(16).padStart(6, '0')}`;
const LAYER_HIGHLIGHT_TEXT_COLOR = `#${LAYER_HIGHLIGHT_COLOR.toString(16).padStart(6, '0')}`;

export class FloorPlanDxfModel extends BaseModel2d {
  private _textCanvasMesh: TextCanvasMesh | null = null;
  private _layerMeshes: Map<string, THREE.LineSegments> = new Map();
  private _lineIndexLayerMap: Map<number, string> = new Map();
  private _allTextList: IDXFTextInfo[] = [];
  private _visibleTextList: IDXFTextInfo[] = [];
  private _hiddenLayers: Set<string> = new Set();
  private _quadTree: IDXFRenderContext['quadTree'] | null = null;
  private _highlightedLayer: string | null = null;
  private _pickRaycaster = new THREE.Raycaster();
  private _pickMouseNdc = new THREE.Vector2();
  private _linePickRadiusPx = 8;
  private _linePickRadiusMin = 40;
  private _linePickRadiusMax = 360;
  private _pickPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private _pickPoint = new THREE.Vector3();
  private _renderGeneration = 0;

  constructor() {
    super();
    this._modelGroup.userData.type = EEntityType.FloorPlan;
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  override setVisible(visible: boolean) {
    if (visible) {
      for (const [key, mesh] of this._layerMeshes.entries()) {
        setModelVisible(mesh, !this._hiddenLayers.has(key));
      }
      this._refreshTextMesh();
    } else {
      for (const layers of this._layerMeshes.values()) {
        setModelVisible(layers, visible);
      }
      // 隐藏时不区分图层，文字全部隐藏
      this._textCanvasMesh?.setTextList([]);
      this._textCanvasMesh?.refresh();
    }
  }

  async render(context: IDXFRenderContext | null) {
    this.clear();

    if (!context) return;

    const gen = ++this._renderGeneration;
    const group = await this._createModel(context, gen);
    // 如果在异步过程中又触发了新的 render，则丢弃本次结果
    if (gen !== this._renderGeneration) return;
    this._modelGroup.add(group);
  }

  private async _createModel(context: IDXFRenderContext, generation: number) {
    const dxfGroup = new THREE.Group();

    if (!context) return dxfGroup;

    this._quadTree = context.quadTree;

    const scaleGroup = new THREE.Group();
    scaleGroup.scale.set(1 / pixelRatio2d, 1 / pixelRatio2d, 1);

    // 按 layer 分组创建 LineSegments，支持独立显隐控制
    // 分批处理 layer 以避免长时间阻塞主线程
    const entries = Object.entries(context.layerLineIndices);
    const CHUNK_SIZE = 10;
    let processedLines = 0;

    for (let ci = 0; ci < entries.length; ci += CHUNK_SIZE) {
      const chunk = entries.slice(ci, ci + CHUNK_SIZE);

      for (const [layer, indices] of chunk) {
        const material = new THREE.LineBasicMaterial({ color: LAYER_LINE_COLOR });
        // 每条线段 2 个顶点，每个顶点 3 个分量（xyz），z=0 由 Float32Array 默认值提供
        const arr = new Float32Array(indices.length * 6);
        for (let i = 0; i < indices.length; i++) {
          const lineIndex = indices[i];
          const offset = lineIndex * 4;
          const base = i * 6;
          arr[base] = context.lineSegArr[offset];
          arr[base + 1] = context.lineSegArr[offset + 1];
          arr[base + 3] = context.lineSegArr[offset + 2];
          arr[base + 4] = context.lineSegArr[offset + 3];
          this._lineIndexLayerMap.set(lineIndex, layer);
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(arr, 3));
        const mesh = new THREE.LineSegments(geometry, material);
        mesh.userData.layerName = layer;
        this._layerMeshes.set(layer, mesh);
        scaleGroup.add(mesh);
        processedLines += indices.length;
      }

      // 每处理 CHUNK_SIZE 个 layer 或累计线段数较多时，让出主线程
      if (ci + CHUNK_SIZE < entries.length && processedLines > 50000) {
        processedLines = 0;
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        if (generation !== this._renderGeneration) return dxfGroup;
      }
    }
    // 文字
    this._allTextList = context.textList;
    this._visibleTextList = context.textList;
    this._textCanvasMesh = new TextCanvasMesh();
    this._textCanvasMesh.setTextColors(LAYER_DEFAULT_TEXT_COLOR, LAYER_HIGHLIGHT_TEXT_COLOR);
    this._textCanvasMesh.setHighlightLayer(this._highlightedLayer);
    this._refreshTextMesh();

    scaleGroup.add(this._textCanvasMesh);
    dxfGroup.add(scaleGroup);

    return dxfGroup;
  }

  /**
   * 批量设置多个 layer 的显隐状态，只触发一次文字刷新，避免逐个调用时的重复渲染开销
   */
  public setLayersVisible(updates: Record<string, boolean>): void {
    for (const [layerName, visible] of Object.entries(updates)) {
      const mesh = this._layerMeshes.get(layerName);
      if (mesh) mesh.visible = visible;

      if (visible) {
        this._hiddenLayers.delete(layerName);
      } else {
        this._hiddenLayers.add(layerName);
      }
    }
    this._refreshTextMesh();
  }

  /**
   * 设置指定 layer 的高亮状态（用于鼠标 hover 反馈）
   * @param layerName 要高亮的 layer 名，null 表示取消高亮
   */
  public setLayerHighlight(layerName: string | null): void {
    if (this._highlightedLayer === layerName) return;

    // 恢复上一个高亮 layer 的颜色
    if (this._highlightedLayer !== null) {
      const prevMesh = this._layerMeshes.get(this._highlightedLayer);
      if (prevMesh) {
        (prevMesh.material as THREE.LineBasicMaterial).color.setHex(LAYER_LINE_COLOR);
      }
    }
    this._highlightedLayer = layerName;
    if (layerName !== null) {
      const mesh = this._layerMeshes.get(layerName);
      if (mesh) {
        (mesh.material as THREE.LineBasicMaterial).color.setHex(LAYER_HIGHLIGHT_COLOR);
      }
    }

    if (this._textCanvasMesh) {
      this._textCanvasMesh.setHighlightLayer(layerName);
      this._textCanvasMesh.refresh();
    }
  }

  /**
   * 根据鼠标位置拾取对应的 layer，优先返回线段 layer，如果没有命中线段再判断文字 layer
   * @param mousePos  归一化鼠标位置坐标（-1 ~ 1）
   * @returns 被拾取的 layer 名，null 表示没有命中任何 layer
   */
  public getLayerAtPoint(mousePos: IPoint2d): string | null {
    this._pickMouseNdc.set(mousePos.x, mousePos.y);
    this._pickRaycaster.setFromCamera(this._pickMouseNdc, globalStores.editorStateStore.sceneInfo.scene2d.camera);

    const pickPoint = this._pickRaycaster.ray.intersectPlane(this._pickPlane, this._pickPoint);
    if (!pickPoint) return null;

    const dxfX = pickPoint.x * pixelRatio2d;
    const dxfY = pickPoint.y * pixelRatio2d;

    const isTextHit = this._getTextLayerAtDxfPoint(dxfX, dxfY);

    if (isTextHit) {
      return isTextHit;
    }

    // quadTree 查询复杂度远低于对所有 LineSegments 做 Raycaster 检测。
    if (this._quadTree && this._lineIndexLayerMap.size > 0) {
      const nearestLineIndex = this._quadTree.queryPointNearestLine(dxfX, dxfY, this._getLinePickRadiusInDxf());
      if (nearestLineIndex !== -1) {
        const layerName = this._lineIndexLayerMap.get(nearestLineIndex);
        if (layerName && !this._hiddenLayers.has(layerName)) {
          return layerName;
        }
      }
    }

    return null;
  }

  private _getLinePickRadiusInDxf(): number {
    const { scene2d, renderer } = globalStores.editorStateStore.sceneInfo;
    const viewportWidth = renderer.domElement.getBoundingClientRect().width || renderer.domElement.clientWidth || 1;
    const worldPerPixel = (scene2d.camera.right - scene2d.camera.left) / (viewportWidth * scene2d.camera.zoom);
    const dxfPerPixel = worldPerPixel * pixelRatio2d;
    return THREE.MathUtils.clamp(
      dxfPerPixel * this._linePickRadiusPx,
      this._linePickRadiusMin,
      this._linePickRadiusMax
    );
  }

  private _getTextLayerAtDxfPoint(dxfX: number, dxfY: number): string | null {
    if (this._visibleTextList.length === 0) return null;

    let layerName: string | null = null;
    let minArea = Infinity;

    for (const textInfo of this._visibleTextList) {
      const { left, right, top, bottom } = textInfo.rotatedBounding;
      if (dxfX < left || dxfX > right || dxfY < bottom || dxfY > top) continue;

      const area = (right - left) * (top - bottom);
      if (area < minArea) {
        minArea = area;
        layerName = textInfo.layer;
      }
    }

    return layerName;
  }

  private _refreshTextMesh(): void {
    if (!this._textCanvasMesh) return;
    const visibleTexts =
      this._hiddenLayers.size === 0
        ? this._allTextList
        : this._allTextList.filter((t) => !this._hiddenLayers.has(t.layer));
    this._visibleTextList = visibleTexts;
    this._textCanvasMesh.setTextList(visibleTexts);
    this._textCanvasMesh.refresh();
  }

  override clear() {
    super.clear();
    this._textCanvasMesh = null;
    this._layerMeshes.clear();
    this._lineIndexLayerMap.clear();
    this._quadTree = null;
    this._allTextList = [];
    this._visibleTextList = [];
    this._hiddenLayers.clear();
    this._highlightedLayer = null;
  }

  public override dispose() {
    super.dispose();
    this._textCanvasMesh = null;
  }
}
